import {
  BadgeColorScheme,
  FieldArrayWithId,
  useDebounce,
  useFieldArray,
  useForm,
  UseFormReturn,
} from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  CommonCropPledgeFarmDocumentationSerializer,
  CommonCropPledgeFarmInformationSerializer,
  CommonCropPledgeLotAffidavitSerializer,
  CommonCropPledgeLotInformationSerializer,
  DocumentSerializer,
  LocalitySerializer,
} from '@bcba/serializers';
import { LocaleKeys } from '@bcba/translations';
import {
  CROP_PLEDGE_USER_ROLES,
  CropPledgeUserRole,
  LEASE_CONTRACT_TYPES,
  LeaseContractType,
  OWNERSHIP_TYPES,
  OwnershipType,
  PLEDGE_SECTION_STATUSES,
  PledgeSectionStatus,
  Product,
  PRODUCTS,
  SelectOption,
} from '@bcba/types';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { validateCuitAfip } from '../../../../../services/company';
import { approveCropPledgeFarmInformation } from '../../../../../services/crop-pledge';
import { ExplorerUpsertCropPledgeFarmInformationDto } from '../../../../../services/crop-pledge/dto/update';
import { getCropPledgeFarmInformation } from '../../../../../services/crop-pledge/farm-information/get-crop-pledge-farm-information';
import { patchCropPledgeFarmInformation } from '../../../../../services/crop-pledge/farm-information/patch-crop-pledge-farm-information';
import { getProvinceLocalities } from '../../../../../services/provinces/getLocalities';
import { getProvinces } from '../../../../../services/provinces/getProvinces';
import { useCropPledgeContext } from '../../context/CropPledgeContext';

const resolver = classValidatorResolver(
  ExplorerUpsertCropPledgeFarmInformationDto,
);

type CropPledgeFarmContextType = {
  data?: CommonCropPledgeFarmInformationSerializer;
  isLoading: boolean;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<CommonCropPledgeFarmInformationSerializer, Error>
  >;
  cropPledgeSectionsStatusBadgeColors: Record<
    (typeof PLEDGE_SECTION_STATUSES)[keyof typeof PLEDGE_SECTION_STATUSES],
    BadgeColorScheme
  >;
  role: CropPledgeUserRole;
  farmInfoStatus: PledgeSectionStatus;
  formMethods: UseFormReturn<
    ExplorerUpsertCropPledgeFarmInformationDto,
    any,
    undefined
  >;
  onSubmit: () => void;
  isLoadingProvinces: boolean;
  provincesOptions: SelectOption[];
  getProvinceLocalitiesOptions: (
    searchValue: string,
  ) => Promise<SelectOption[]>;
  hasSelectedProvince: boolean;
  defaultHasPropietaryInfo: OwnershipType;
  leaseContractTypeOptions: SelectOption[];
  lotsFields: FieldArrayWithId<
    ExplorerUpsertCropPledgeFarmInformationDto,
    'lots',
    'id'
  >[];
  onAddLotAffidavit: () => void;
  onRemoveLotAffidavit: (index: number) => void;
  cropsOptions: SelectOption[];
  isPendingRequestApproval: boolean;
  approveSection: UseMutateAsyncFunction<
    CommonCropPledgeFarmInformationSerializer,
    Error,
    void,
    unknown
  >;
  isPendingSectionApproval: boolean;
  isFormReadOnly: boolean;
};

const CropPledgeFarmContext = createContext<
  CropPledgeFarmContextType | undefined
>(undefined);
type CropPledgeFarmProviderProps = {
  children: ReactNode;
};

const mapCropPledgeFarmDocument = (
  doc?: CommonCropPledgeFarmDocumentationSerializer,
) => ({
  uuid: doc?.uuid || undefined,
  documentUuid: doc?.document?.uuid || undefined,
  keepOriginal: Boolean(doc?.uuid),
  originalName: doc?.document?.originalFileName || '',
  identifier: doc?.document?.identifier,
  expirationDate: doc?.document?.expirationDate,
});

const mapDocument = (doc?: DocumentSerializer) => ({
  uuid: doc?.uuid || undefined,
  keepOriginal: Boolean(doc?.uuid),
  originalName: doc?.originalFileName || '',
});

const mapLotAffidavitSerializerToForm = (
  data?: CommonCropPledgeLotAffidavitSerializer,
) => {
  return {
    averageYieldPreviousSeason: data?.averageYieldPreviousSeason as number,
    averageYieldPreviousSeason1: data?.averageYieldPreviousSeason1 as number,
    averageYieldPreviousSeason2: data?.averageYieldPreviousSeason2 as number,
    endsAt: Number(data?.endsAt) as number,
    startsAt: Number(data?.startsAt) as number,
    uuid: data?.uuid ?? '',
    productType: data?.product as Product,
  };
};

const mapLotInformationSerializerToForm = (
  data?: CommonCropPledgeLotInformationSerializer,
) => {
  return {
    affidavit: mapLotAffidavitSerializerToForm(data?.affidavit ?? undefined),
    geolocationDocument: mapDocument(data?.geolocationDocument ?? undefined),
    identifier: data?.identifier ?? '',
    uuid: data?.uuid ?? '',
  };
};

const mapCropPledgeFarmInformationSerializerToForm = (
  data?: CommonCropPledgeFarmInformationSerializer,
): ExplorerUpsertCropPledgeFarmInformationDto => {
  return {
    cadastralNomenclature: data?.cadastralNomenclature ?? '',
    documentation: data?.additionalDocumentation?.length
      ? data?.additionalDocumentation?.map((doc) =>
          mapCropPledgeFarmDocument(doc),
        )
      : [{ keepOriginal: false }],
    geolocationDocument: {
      uuid: data?.geolocationDocument?.uuid,
      keepOriginal: Boolean(data?.geolocationDocument),
    },
    farmIdentifier: data?.farmIdentifier ?? '',
    localityUuid: data?.locality?.uuid ?? '',
    provinceUuid: data?.province?.uuid ?? '',
    address: data?.address ?? '',
    postalCode: data?.postalCode ?? '',
    totalHectares: data?.totalHectares
      ? Number(data?.totalHectares)
      : (undefined as unknown as number),
    ownershipType: data?.ownershipType ?? OWNERSHIP_TYPES.OWN,
    leaseContractType: (data?.leaseContractType ??
      undefined) as LeaseContractType,
    leasedFrom: Number(data?.leasedFrom) ?? undefined,
    leasedUntil: Number(data?.leasedUntil) ?? undefined,
    ownerFiscalId: data?.ownerFiscalId ?? '',
    lots: data?.lotsInformation?.length
      ? data.lotsInformation.map(mapLotInformationSerializerToForm)
      : [mapLotInformationSerializerToForm()],
  };
};

export const CropPledgeFarmProvider: React.FC<CropPledgeFarmProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const cropPledgeUuid = params['uuid'] || '';
  const {
    cropPledgeSectionsStatusBadgeColors,
    role,
    refetchCropPledgeStatus,
    isPledgeInAnEditableStage,
  } = useCropPledgeContext();
  const { showSuccessToast } = useToastsHook();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['get-crop-pledge-farm-information', cropPledgeUuid],
    queryFn: () => getCropPledgeFarmInformation(cropPledgeUuid),
  });

  const { data: provinces = [], isLoading: isLoadingProvinces } = useQuery({
    queryKey: ['provinces'],
    queryFn: async () => getProvinces(),
  });

  const provincesOptions: SelectOption[] = useMemo(
    () =>
      provinces?.map(({ name, uuid }) => ({
        label: name,
        value: uuid,
      })) ?? [],
    [provinces],
  );

  const formMethods = useForm<ExplorerUpsertCropPledgeFarmInformationDto>({
    resolver,
    defaultValues: mapCropPledgeFarmInformationSerializerToForm(data),
  });

  const {
    setValue,
    setError,
    watch,
    formState,
    reset,
    clearErrors,
    handleSubmit,
  } = formMethods;

  useEffect(() => {
    reset(mapCropPledgeFarmInformationSerializerToForm(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectedOwnershipType = watch('ownershipType');
  const defaultHasPropietaryInfo: OwnershipType =
    selectedOwnershipType ?? OWNERSHIP_TYPES.OWN;

  const selectedProvinceUuid = watch('provinceUuid');
  const hasSelectedProvince = Boolean(selectedProvinceUuid);

  const selectedLocalityUuid = watch('localityUuid');

  const getProvinceLocalitiesOptions = (searchValue: string) =>
    hasSelectedProvince
      ? getProvinceLocalities(String(selectedProvinceUuid), searchValue).then(
          (res) => {
            const result: SelectOption[] = res.map(
              (locality: LocalitySerializer) => ({
                label: locality.name,
                value: String(locality.uuid),
              }),
            );
            return result;
          },
        )
      : Promise.resolve([]);

  useEffect(() => {
    if (selectedProvinceUuid && selectedLocalityUuid && formState.isDirty) {
      setValue('localityUuid', '');
      setError('localityUuid', {
        type: 'custom',
        message: t('companyContactInformation.form.error.validLocality'),
      });
    }
  }, [selectedProvinceUuid]);

  const { mutateAsync: requestApproval, isPending: isPendingRequestApproval } =
    useMutation({
      mutationFn: (payload: ExplorerUpsertCropPledgeFarmInformationDto) => {
        const additionalDocumentFiles = payload.documentation;
        if (additionalDocumentFiles) {
          additionalDocumentFiles.forEach((doc: any) => {
            doc.expirationDate = undefined;
            doc.identifier = undefined;
            doc.documentUuid = undefined;
          });
        }
        return patchCropPledgeFarmInformation(cropPledgeUuid, payload);
      },
      onSuccess: () => {
        refetch();
        refetchCropPledgeStatus();
        showSuccessToast({ title: t('common.requestApproval.toast.success') });
      },
    });

  const { mutateAsync: approveSection, isPending: isPendingSectionApproval } =
    useMutation({
      mutationFn: () => approveCropPledgeFarmInformation(cropPledgeUuid),
      onSuccess: () => {
        refetch();
        refetchCropPledgeStatus();
        showSuccessToast({ title: t('common.approve.toast.success') });
      },
    });

  const ownerFiscalIdKey = 'ownerFiscalId';

  const { mutate: syncValidateFiscalId, isError: isErrorValidateFiscalId } =
    useMutation({
      mutationKey: ['validate-fiscal-id-afip'],
      mutationFn: validateCuitAfip,
      onSuccess: () => clearErrors(ownerFiscalIdKey),
      onError: (error) => {
        const errorResponse = error as any;
        const errorCode = errorResponse?.code;
        setError(ownerFiscalIdKey, {
          message: t(`error.${errorCode}.errorDescription` as LocaleKeys),
        });
      },
    });

  const ownerFiscalId = watch(ownerFiscalIdKey);
  const debouncedOwnerFiscalId = useDebounce(ownerFiscalId, 500);

  useEffect(() => {
    if (debouncedOwnerFiscalId) syncValidateFiscalId(debouncedOwnerFiscalId);
  }, [debouncedOwnerFiscalId]);

  const onSubmit = () => {
    if (role === CROP_PLEDGE_USER_ROLES.DEBTOR && !isErrorValidateFiscalId) {
      handleSubmit((data) => requestApproval(data))();
    }
  };

  const leaseContractTypeOptions = Object.values(LEASE_CONTRACT_TYPES).map(
    (value) => ({
      label: t(`common.LEASE_CONTRACT_TYPES.${value}.title`),
      value,
    }),
  );

  const cropsOptions = Object.values(PRODUCTS).map((value) => ({
    label: t(`product.${value}`),
    value,
  }));

  const {
    fields: lotsFields,
    append,
    remove,
  } = useFieldArray({
    name: 'lots',
    control: formMethods.control,
  });

  const onAddLotAffidavit = () =>
    append(mapLotInformationSerializerToForm() as any);

  const onRemoveLotAffidavit = (index: number) => remove(index);

  const farmInfoStatus = (data?.status ??
    PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD) as PledgeSectionStatus;

  const isFormReadOnly =
    !isPledgeInAnEditableStage ||
    role === CROP_PLEDGE_USER_ROLES.CREDITOR ||
    (role === CROP_PLEDGE_USER_ROLES.DEBTOR &&
      farmInfoStatus !== PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD &&
      farmInfoStatus !== PLEDGE_SECTION_STATUSES.PENDING_BOLSA_LOAD);

  return (
    <CropPledgeFarmContext.Provider
      value={{
        data,
        isLoading,
        refetch,
        cropPledgeSectionsStatusBadgeColors,
        role,
        farmInfoStatus,
        formMethods,
        onSubmit,
        isLoadingProvinces,
        provincesOptions,
        getProvinceLocalitiesOptions,
        hasSelectedProvince,
        defaultHasPropietaryInfo,
        leaseContractTypeOptions,
        lotsFields,
        onAddLotAffidavit,
        onRemoveLotAffidavit,
        cropsOptions,
        isPendingRequestApproval,
        approveSection,
        isPendingSectionApproval,
        isFormReadOnly,
      }}
    >
      {children}
    </CropPledgeFarmContext.Provider>
  );
};

export const useCropPledgeFarmContext = (): CropPledgeFarmContextType => {
  const context = useContext(CropPledgeFarmContext);
  if (context === undefined) {
    throw new Error(
      'useCropPledgeFarmContext must be used within a CropPledgeFarmProvider',
    );
  }
  return context;
};
